import React, { useCallback, useMemo, useRef } from 'react';
import { useSettings } from 'services/settings/SettingsContext';
import { useModal } from 'components/modals/ModalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useHistory } from 'react-router-dom';
import { ListColumnType, ListFilterType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import CheckButton from 'components/buttons/CheckButton';
import { renderDateOrDateTime } from 'components/lists/functions/renderListColumn';
import { determineTaskDateClass } from 'services/utils/dateTimeUtils';
import { useTranslation } from 'react-i18next';
import ProposalModal from '../../views/proposals/ProposalModal';
import OrderModal from '../../views/order/OrderModal';
import JobModal from '../../views/jobs/JobModal';
import InvoiceModal from '../../views/invoices/InvoiceModal';
import TaskForm from './TaskForm';
import { convertLocalDateTimeToUTC } from 'internationalization/timezoneConversions';

const TaskList: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { userHash } = useAuthContext();
    const { userLocale, userTimezone } = useSettings();
    const { initializeModal } = useModal();
    const refreshListRef = useRef<() => void>(() => {});
    const taskForeignKeyFields = ['organization', 'person', 'location', 'deal', 'proposal', 'order', 'purchase', 'job', 'invoice'];

    // Set the task status class name to style the task rows based on its status
    const setTaskStatusClassName = (date: string | null, due_date: string, checked: boolean): string => {

        // Determine if the task is a whole day task
        const taskIsWholeDay = date !== null;

        // Get the date class of the task
        const dateClass = determineTaskDateClass(due_date, taskIsWholeDay, checked);

        return dateClass;
    };

    // Render linked object details
    const renderLinkedObject = (row: any): JSX.Element => {

        // Find the first foreign key field with a value
        const foundObject = taskForeignKeyFields.find((field) => !!row[field]);

        // When an object is found, render the linked object details. Otherwise render a dash
        if (foundObject) {
            return (
                <div>
                    {t('task.general.linked_object_name', { object_name: t(`${foundObject}.general.object_name.singular`) })} {row[`linked_${foundObject}_details`]}
                </div>
            );
        } else {
            return <div>-</div>;
        }
    };

    // Handle task row click to open different type of pages or modals
    const handleTaskRowClick = (row: any) => {

        // Find the first foreign key field with a value
        const linkedObject = taskForeignKeyFields.find((field) => !!row[field]);
    
        // Modal mapping for objects which have a detail modal
        const modalMapping: Record<string, React.FC<{ itemId?: number }>> = {
            proposal: ProposalModal,
            order: OrderModal,
            job: JobModal,
            invoice: InvoiceModal,
        };
    
        // Url mapping for objects which have a detail page
        const urlMapping: Record<string, string> = {
            organization: 'contacts/organization',
            person: 'contacts/persons',
            location: 'contacts/work-locations',
            deal: 'deals',
        };

        // Get the id of the linked object. For example: 'proposal: 59'
        const objectId = linkedObject ? row[linkedObject] : null;

        // Handle opening of modal or pages
        if (linkedObject && objectId) {

            // If a modal needs to be opened, get the modal component for the object and open it
            if (modalMapping[linkedObject]) {
                const ModalComponent = modalMapping[linkedObject];
                initializeModal(
                    <ModalComponent itemId={objectId} />,
                    { modalSize: 'large' }
                );
                return;
            }
    
            // If a page needs to be opened, open the page
            if (urlMapping[linkedObject]) {
                history.push(`/${urlMapping[linkedObject]}/${objectId}`);
                return;
            }
        }

        // If no object is linked, open the task form 
        initializeModal(
            <TaskForm taskId={row.id} placement="modal" />,
            { 
                modalSize: 'small-medium',
                title: t('button.edit_object_label', { object_name: t('task.general.object_name.singular') }),
                onSuccess: () => {if (refreshListRef.current) { refreshListRef.current() }}
            }
        );
    };

    // Specify the columns of this list
    const columns: ListColumnType[] = useMemo(() => [
        { 
            width: '68px', 
            field: 'checked', 
            label: 'task.general.done_label', 
            orderField: 'checked',
            render: (_, row) => (
                <CheckButton
                    taskId={row.id}
                    checked={row.checked}
                    onSuccess={refreshListRef.current}
                />
            ),
            customClass: 'check-button-column'
        },
        { 
            width: '2fr', 
            field: 'subject', 
            label: 'task.general.subject_label',
            customClass: (_, row) => setTaskStatusClassName(row.date, row.due_date, row.checked)
        },
        { 
            width: '2fr', 
            field: 'note_plain', 
            disableOrdering: true,
            label: 'task.general.note_label',
            customClass: (_, row) => setTaskStatusClassName(row.date, row.due_date, row.checked) 
        },
        { 
            width: '1.5fr', 
            field: 'assignee_name',
            orderField: 'assignee', 
            label: 'task.general.assigned_to_label' ,
            customClass: (_, row) => setTaskStatusClassName(row.date, row.due_date, row.checked)
        },
        { 
            width: '1.5fr', 
            field: 'due_date',
            label: 'task.general.due_date_label',
            render: (_, row) => renderDateOrDateTime(row.due_date, userLocale),
            customClass: (_, row) => setTaskStatusClassName(row.date, row.due_date, row.checked)
        },
        { 
            width: '2fr', 
            field: 'linked_object',
            disableOrdering: true,
            label: 'task.general.linked_to_label',
            render: (_, row) => renderLinkedObject(row),
            customClass: (_, row) => setTaskStatusClassName(row.date, row.due_date, row.checked)
        },
    ], []);

    // Convert the current date time into utc based on the timezone of the user to be used for the my due tasks filter
    const currentDateTime = new Date();
    const utcDateTime = convertLocalDateTimeToUTC(currentDateTime, userLocale, userTimezone);

    // Set the filters for the list
    const filters: ListFilterType[] = [
        {
            id: 'my_tasks_due',
            label: 'task.general.my_due_tasks_filter_label',
            endpoint: 'get_task_list',
            params: {
                assignee_hash: userHash,
                due_on_before: utcDateTime,
                checked: false
            },
            default: true,
            quickFilter: true,
        },
        {
            id: 'my_tasks',
            label: 'task.general.my_tasks_filter_label',
            endpoint: 'get_task_list',
            params: {
                assignee_hash: userHash,
            },
            quickFilter: true,
        },
        {
            id: 'all_tasks',
            label: 'task.general.all_tasks_filter_label',
            endpoint: 'get_task_list',
            quickFilter: true,
        }
    ]

    return (
        <AbstractList
            query={{
                endpoint: 'get_task_list',
                object: 'task'
            }}
            objectName='task.general.object_name'
            columns={columns}
            filters={filters}
            formModal={<TaskForm placement='modal' />}
            modalSize='small-medium'
            isPaginated={true}
            onRowClick={handleTaskRowClick}
            setRefetchList={
                useCallback((fn: () => void) => {
                    refreshListRef.current = fn;
                }, [])
            }
        />
    );
};

export default TaskList;